export class Constants {
  static readonly LOGIN_ROUTE = "/auth/login";
  static readonly DASHBOARD_ROUTE = "/";
  static readonly ROLES_ROUTE = "/roles";
  static readonly FEATURE_FLAGS_ROUTE = "/feature-flags";
  static readonly ACTIVITY_ROUTE = "/activity";
  static readonly APP_RELEASE_VERSIONS_ROUTE = "/app-releases";
  static readonly IV3_MOBILE_ROUTE = "/iv3-mobile";
  static readonly DYNAMIC_LINKS_ROUTE = "/dynamic-links";
  static readonly APP_FAQ_ROUTE = "/app-faq";
  static readonly HAPPENING_NOW_ROUTE = "/happening-now";
  /**
   * Legacy classic composer path. The classic composer has been retired; the
   * route now redirects to {@link HAPPENING_NOW_COMPOSE_ROUTE}. Kept so old
   * bookmarks and deep links still resolve.
   */
  static readonly HAPPENING_NOW_CREATE_ROUTE = "/happening-now/create";
  /** Carousel-first composer (multi-image) — the only Happening Now composer. */
  static readonly HAPPENING_NOW_COMPOSE_ROUTE = "/happening-now/compose";
  static readonly BROADCASTS_ROUTE = "/broadcasts";
  static readonly PORTAL_NOTIFICATIONS_ROUTE = "/portal-notifications";
  static readonly IMAGE_HOSTING_ROUTE = "/image-hosting";
  /** Base URL for short links (no trailing slash). Override with REACT_APP_PUBLIC_SITE_URL. */
  static readonly DYNAMIC_LINKS_PUBLIC_BASE = (
    process.env.REACT_APP_PUBLIC_SITE_URL || "https://ivueit.web.app"
  ).replace(/\/$/, "");

  // GENERAL CONSTANTS
  static readonly VIEW_ONLY_PERMISSIONS_TEXT =
    "View only — You are not authorized to make changes.";

  //  IV3 MOBILE CONSTANTS
  static readonly IV3_MOBILE_CODE_COVERAGE_URL =
    "https://ivueit.gitlab.io/iv3mobile/";
  static readonly IV3_MOBILE_CRASHLYTICS_URL =
    "https://console.firebase.google.com/u/0/project/ivueit3/crashlytics";
  static readonly IV3_MOBILE_SENTRY_URL =
    "https://ivueit.sentry.io/insights/projects/ivueit/?project=5429850";
  static readonly IV3_MOBILE_BILLING_URL =
    "https://console.cloud.google.com/billing/0059A2-40B5B4-58AAFF/reports;projects=ivueit3?authuser=0&project=ivueit3&hl=en-US";
  static readonly IV3_MOBILE_GOOGLE_PLAY_CONSOLE_URL =
    "https://play.google.com/console/u/0/developers/7571792612147639836/app/4973471646108329365/app-dashboard";
  static readonly IV3_MOBILE_APP_STORE_CONNECT_URL =
    "https://appstoreconnect.apple.com/apps/1212205667/distribution";

  static readonly IV3_MOBILE_DATADOG_URL =
    process.env.REACT_APP_DATADOG_URL ||
    "https://app.datadoghq.com/logs?query=service%3Aiv3mobile";

  // COLLECTIONS
  static readonly DYNAMIC_LINKS_COLLECTION = "dynamicLinks";
  /** Root collection that groups all Happening-Now data under one node. */
  static readonly HAPPENING_NOW_ROOT_COLLECTION = "happeningNow";
  /** Live carousel — what the mobile app reads. Only mutated via the publish flow. */
  static readonly HAPPENING_NOW_COLLECTION = "happeningNow/live/items";
  /**
   * Parent doc of the live `items` subcollection. Holds carousel-wide settings
   * (e.g. `parallaxEnabled`) that mobile reads alongside the live cards.
   */
  static readonly HAPPENING_NOW_LIVE_DOC = "happeningNow/live";
  /** Operator's working draft — CMS reads/writes here freely. */
  static readonly HAPPENING_NOW_DRAFT_COLLECTION = "happeningNow/drafts/items";
  /** Append-only history of past published carousels. */
  static readonly CAROUSEL_PUBLICATIONS_COLLECTION =
    "happeningNow/publications/items";

  // HAPPENING NOW CONSTANTS
  static readonly HAPPENING_NOW_STORAGE_PREFIX = "happening-now";
  static readonly HAPPENING_NOW_TITLE = "What's Happening Now?";
  static readonly HAPPENING_NOW_COMPOSE_PANEL_DESCRIPTION =
    "Share what's happening now with your audience. Build the draft and publish in one click.";
  static readonly HAPPENING_NOW_DESCRIPTION =
    "Publish Beautiful Stories with a Click";
  static readonly HAPPENING_NOW_IMAGE_DESCRIPTION =
    "Drop a 16:9 image. PNG or JPG, 2 MB max.";
  static readonly HAPPENING_NOW_DETAILS_DESCRIPTION =
    "Title, link, and when it should stop showing.";
  static readonly HAPPENING_NOW_IMAGE_MAX_BYTES = 2 * 1024 * 1024;
  /** Days after expiresAt at which the janitor sets markedForDeletion. */
  static readonly HAPPENING_NOW_MARK_AFTER_DAYS = 30;
  /** Days after expiresAt at which the janitor hard-deletes. */
  static readonly HAPPENING_NOW_DELETE_AFTER_DAYS = 45;
  /** Seed value (days from now) for a new card's expiry in the composer. */
  static readonly HAPPENING_NOW_DEFAULT_EXPIRY_DAYS = 7;
  /** A scheduled `startsAt` can be at most this far in the future (~1 month). */
  static readonly HAPPENING_NOW_MAX_START_AHEAD_DAYS = 31;
  /** Days a `carouselPublications` doc is kept before the janitor purges it (unless pinned). */
  static readonly CAROUSEL_PUBLICATION_RETENTION_DAYS = 60;
  /** Default lookback (days) shown in the "Past carousels" panel. */
  static readonly CAROUSEL_HISTORY_DEFAULT_DAYS = 60;
  static readonly STARRED_ONLY_DESCRIPTION = "Showing starred carousels only.";
  static readonly HAPPENING_NOW_REPUBLISH_DESCRIPTION =
    "Pick the cards to bring back, set a new expiry, then publish from the main panel when you're ready.";
  static readonly REPUBLISH_EXPIRY_DESCRIPTION =
    "New expiry for restored cards";

  // Release Constants
  static readonly APP_RELEASE_VERSIONS_DESCRIPTION =
    "Control forced vs optional updates, status banner, and reminder cadence. Stored at app_config/version_control.";
  static readonly NETWORK_ERROR_DESCRIPTION =
    "Check your connection, then try again.";
  static readonly APP_RELEASE_THRESHOLDS_DESCRIPTION =
    "Set the floor for forced updates and the line for optional nudges.";
  static readonly APP_RELEASE_MINIMUM_BUILD_DESCRIPTION =
    "Builds below this number must update before continuing.";
  static readonly APP_RELEASE_RECOMMENDED_BUILD_DESCRIPTION =
    "Below this, clients see an optional update prompt (unless you force below).";
  static readonly APP_RELEASE_BEHAVIOR_AND_COPY_DESCRIPTION =
    "Messaging and how often to remind about optional updates.";
  static readonly APP_RELEASE_STATUS_BANNER_DESCRIPTION =
    "Show a dismissible top banner in the mobile app (e.g. backend slowdowns).";
  static readonly APP_RELEASE_STATUS_BANNER_MESSAGE_DESCRIPTION =
    "Leave blank to use the app’s default slow-response message.";

  //  DYNAMIC LINKS CONSTANTS

  static readonly ANDROID_PLACEHOLDER = "https://play.google.com/...";
  static readonly IOS_PLACEHOLDER = "https://apps.apple.com/...";
  static readonly FALLBACK_PLACEHOLDER = "https://…";
  static readonly DYNAMIC_LINKS_TITLE = "Send people to the right app";
  static readonly DYNAMIC_LINKS_DESCRIPTION =
    "Pick Android, iOS, and web fallbacks — one short link does the rest.";
  static readonly DYNAMIC_LINKS_REQUIRED_DESCRIPTION =
    "Required — we route by device automatically.";
  static readonly DYNAMIC_LINKS_WHERE_DESCRIPTION = "Where should taps go?";
  static readonly DYNAMIC_LINKS_OPTIONAL_EXTRAS_DESCRIPTION =
    "Name it, pick a slug, pause it, or set an expiry.";

  static readonly APP_FAQ_DESCRIPTION =
    "Help articles for Vuers to help them use the app.";

  static readonly APP_FAQ_NOTE =
    "Note: These articles take upto 24 hours to reflect changes. Logout and login to see the changes immediately.";

  // IMAGE HOSTING CONSTANTS
  /** Firestore metadata collection for hosted images. */
  static readonly IMAGE_HOSTING_COLLECTION = "hostedImages";
  /** Public short-link path segment (full URL: `{PUBLIC_BASE}/img/{id}`). */
  static readonly IMAGE_HOSTING_SHORT_PATH = "/img";
  /** Storage prefix — objects live under `image-hosting/{uid}/{id}/…`. */
  static readonly IMAGE_HOSTING_STORAGE_PREFIX = "image-hosting";
  static readonly IMAGE_HOSTING_MAX_BYTES = 10 * 1024 * 1024;
  static readonly IMAGE_HOSTING_DEFAULT_EXPIRY_DAYS = 30;
  static readonly IMAGE_HOSTING_MAX_EXPIRY_DAYS = 180;
  static readonly IMAGE_HOSTING_TITLE = "Image hosting";
  static readonly IMAGE_HOSTING_DESCRIPTION =
    "Upload an image, get a shareable link. Links expire after 30 days by default — choose up to 180.";
  static readonly IMAGE_HOSTING_UPLOAD_HINT =
    "Drop an image here or click to choose — 10 MB max.";

  // FEEDBACK CONSTANTS
  static readonly FEEDBACK_COLLECTION = "feedback";
  /** Reports older than this are purged by the `cleanupJanitor` function. */
  static readonly FEEDBACK_RETENTION_DAYS = 180;

  // BROADCASTS CONSTANTS
  static readonly NOTIFICATION_HISTORY_COLLECTION = "notificationHistory";
  static readonly BROADCASTS_TITLE = "Broadcasts";
  static readonly BROADCASTS_DESCRIPTION =
    "A Convenient way to Broadcast Promotional or News Notifications to your users.";
  static readonly BROADCASTS_HISTORY_RETENTION_DAYS = 30;
  static readonly BROADCASTS_DELETE_ENTRY_TITLE = " Delete this Broadcast?";
  static readonly BROADCASTS_DRAFT_DELETE_DESCRIPTION =
    " draft from the dashboard.";
  static readonly BROADCASTS_DELETE_ENTRY_DESCRIPTION =
    " Removes the broadcast from the dashboard. The broadcast itself was already delivered — this only deletes the dashboard record.";

  /**
   * Days RTDB `activity/` entries are kept before `cleanupActivity` prunes them.
   * Keep in sync with `functions/src/cleanupActivity.ts` (`RETENTION_DAYS`).
   */
  static readonly ACTIVITY_RETENTION_DAYS = 180;
}
